import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  FormControlLabel,
  Chip,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  CircularProgress,
  Divider,
  Stack,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import FactCheckIcon from '@mui/icons-material/FactCheck';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReorderIcon from "@mui/icons-material/Reorder";
import CalendarToday from "@mui/icons-material/CalendarToday";
import LocationOn from "@mui/icons-material/LocationOn";
import FmdGood from "@mui/icons-material/FmdGood";
import LocalShipping from "@mui/icons-material/LocalShipping";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import api from "../../config/axios";
import debounce from "lodash/debounce";
import OrdersMobileView from "../../components/OrdersMobileView";
import OrdersMobileHeader from "../../components/OrdersMobileHeader";


const formatStatus = (status) => {
  return status
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const getStatusIcon = (status) => {
  switch (status) {
    case "all":
      return <AllInboxIcon sx={{ fontSize: '1.1rem' }} />;
    case "pending-approval":
      return <PendingActionsIcon sx={{ fontSize: '1.1rem' }} />;
    case "accepted":
      return <FactCheckIcon sx={{ fontSize: '1.1rem' }} />;
    case "in-progress":
      return <PlayArrowIcon sx={{ fontSize: '1.1rem' }} />;
    case "ready-to-ship":
      return <ReorderIcon sx={{ fontSize: '1.1rem' }} />;
    case "dispatched":
      return <LocalShippingIcon sx={{ fontSize: '1.1rem' }} />;
    case "delivered":
      return <CheckCircleIcon sx={{ fontSize: '1.1rem' }} />;
    case "canceled":
      return <CancelIcon sx={{ fontSize: '1.1rem' }} />;
    default:
      return <AllInboxIcon sx={{ fontSize: '1.1rem' }} />;
  }
};

const Orders = () => {
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("none"); // 'asc', 'desc', or 'none'
  const [statusCounts, setStatusCounts] = useState({});
  const [dateOfMarriageFilter, setDateOfMarriageFilter] = useState("");
  const [filtersReady, setFiltersReady] = useState(false);

  const [showShiprocketStatus, setShowShiprocketStatus] = useState(false);
  const [shiprocketDeliveryStatusByOrderId, setShiprocketDeliveryStatusByOrderId] = useState({});
  const [trackingDataByOrderId, setTrackingDataByOrderId] = useState({});
  const [shiprocketStatusLoadingByOrderId, setShiprocketStatusLoadingByOrderId] = useState({});

  // Prevent out-of-order async responses from overwriting the latest filter results.
  const fetchRequestIdRef = useRef(0);

  // Infinite scroll states
  const [mobileOrders, setMobileOrders] = useState([]);
  const [mobilePage, setMobilePage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  // Tracking modal states
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [trackingUrl, setTrackingUrl] = useState("");
  const [delhiveryData, setDelhiveryData] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // Initialize state from URL parameters
  useEffect(() => {
    const statusFromUrl = searchParams.get("status") || "all";
    const searchFromUrl = searchParams.get("search") || "";
    const paymentFromUrl = searchParams.get("payment") || "";
    const sortFromUrl = searchParams.get("sort") || "none";
    const domFilterFromUrl = searchParams.get("dom") || "";
    const trackingIdFromUrl = searchParams.get("tracking_id") || "";
    const shiprocketStatusFromUrl = searchParams.get("shiprocket_status") === "1";

    setSelectedStatus(statusFromUrl);
    setSearch(searchFromUrl);
    setDebouncedSearch(searchFromUrl);
    setPaymentStatusFilter(paymentFromUrl);
    setSortOrder(sortFromUrl);
    setDateOfMarriageFilter(domFilterFromUrl);
    setShowShiprocketStatus(shiprocketStatusFromUrl);

    // If tracking_id is in URL, set status to dispatched and search for tracking ID
    if (trackingIdFromUrl) {
      setSelectedStatus("dispatched");
      setSearch(trackingIdFromUrl);
      setDebouncedSearch(trackingIdFromUrl);
    }

    setFiltersReady(true);
  }, [searchParams]);

  // Update URL when state changes
  const updateURL = useCallback(
    (updates) => {
      const newParams = new URLSearchParams(searchParams);

      Object.entries(updates).forEach(([key, value]) => {
        if (value === "" || value === 0 || value === "all") {
          newParams.delete(key);
        } else {
          newParams.set(key, value.toString());
        }
      });

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // Debounced search handler
  const debouncedSearchHandler = useCallback(
    debounce((value) => {
      setDebouncedSearch(value);
      setMobilePage(0); // Reset to first page when search changes
      updateURL({ search: value, page: 0 });
    }, 500),
    [updateURL]
  );

  // Update search value and trigger debounced search
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearch(value);
    debouncedSearchHandler(value);
  };


  // Mobile infinite scroll functions
  const fetchMobileOrders = async (pageNumber, reset = false) => {
    const requestId = ++fetchRequestIdRef.current;
    try {
      if (reset) {
        setLoading(true);
        setMobileOrders([]);
        setMobilePage(1);
        setHasMore(true);
      }

      const queryParams = new URLSearchParams({
        page: pageNumber,
        limit: 20, // Fixed limit for mobile
        ...(sortOrder !== "none" && { sort: `dom:${sortOrder}` }),
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(selectedStatus !== "all" && { status: selectedStatus }),
        ...(paymentStatusFilter && { payment_status: paymentStatusFilter }),
        ...(dateOfMarriageFilter && { dom: dateOfMarriageFilter }),
      });

      const response = await api.get(`/orders.php?${queryParams.toString()}`);
      const newOrders = response.data.data.orders || [];
      const totalOrders = response.data.data.total || 0;

      // If another request was started after this one, ignore this response.
      if (requestId !== fetchRequestIdRef.current) return;

      if (reset) {
        setMobileOrders(newOrders);
      } else {
        setMobileOrders(prev => [...prev, ...newOrders]);
      }

      setTotal(totalOrders);
      setHasMore(mobileOrders.length + newOrders.length < totalOrders);
      setLoading(false);
      setLoadingMore(false);
    } catch (error) {
      if (requestId !== fetchRequestIdRef.current) return;
      console.error("Error fetching mobile orders:", error);
      setLoading(false);
      setLoadingMore(false);
    }
  };


  // Fetch status counts for all statuses
  const fetchStatusCounts = async () => {
    try {
      const response = await api.get("/order_status_counts.php");
      const allOrders = response.data.data || [];

      const counts = {
        all: allOrders.all,
        "pending-approval": allOrders["pending-approval"] || 0,
        accepted: allOrders.accepted,
        "in-progress": allOrders["in-progress"],
        "ready-to-ship": allOrders["ready-to-ship"],
        dispatched: allOrders.dispatched,
        delivered: allOrders.delivered,
        canceled: allOrders.canceled,
      };

      setStatusCounts(counts);
    } catch (error) {
      console.error("Error fetching status counts:", error);
    }
  };

  useEffect(() => {
    fetchStatusCounts();
  }, []);

  // Unified infinite scroll effect for both desktop and mobile
  useEffect(() => {
    if (!filtersReady) return;
    fetchMobileOrders(1, true); // Reset and fetch first page
  }, [filtersReady, debouncedSearch, selectedStatus, paymentStatusFilter, dateOfMarriageFilter, sortOrder]);

  // Load more orders when page changes
  useEffect(() => {
    if (mobilePage > 1) {
      fetchMobileOrders(mobilePage, false);
    }
  }, [mobilePage]);

  const loadMoreOrders = useCallback(() => {
    if (!loading && !loadingMore && hasMore) {
      setLoadingMore(true);
      setMobilePage(prev => prev + 1);
    }
  }, [loading, loadingMore, hasMore]);

  // Update status options
  const statusOptions = [
    "all",
    "pending-approval",
    "accepted",
    "in-progress",
    "ready-to-ship",
    "dispatched",
    "delivered",
    "canceled",
  ];
  const paymentStatusOptions = ["pending", "paid-advance", "paid", "refunded"];


  const handleStatusTabChange = (event, newValue) => {
    setSelectedStatus(newValue);
    updateURL({ status: newValue });

    // Reset mobile orders when status changes
    setMobileOrders([]);
    setMobilePage(1);
    setHasMore(true);
  };

  const handlePaymentStatusChange = (event) => {
    const value = event.target.value;
    setPaymentStatusFilter(value);
    updateURL({ payment: value });

    // Reset mobile orders when payment filter changes
    setMobileOrders([]);
    setMobilePage(1);
    setHasMore(true);
  };

  const handleSortToggle = () => {
    let newSortOrder;
    if (sortOrder === "none") {
      newSortOrder = "asc";
    } else if (sortOrder === "asc") {
      newSortOrder = "desc";
    } else {
      newSortOrder = "none";
    }
    setSortOrder(newSortOrder);
    updateURL({ sort: newSortOrder });
  };

  const handleViewOrder = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  const handleDateOfMarriageChange = (newDate) => {
    const dateValue = newDate ? newDate.format('YYYY-MM-DD') : "";
    setDateOfMarriageFilter(dateValue);
    updateURL({ dom: dateValue });

    // Reset mobile orders when date filter changes
    setMobileOrders([]);
    setMobilePage(1);
    setHasMore(true);
  };

  const shiprocketStatusAvailable =
    selectedStatus === "ready-to-ship" || selectedStatus === "dispatched";

  const handleShiprocketStatusSwitch = (event) => {
    const checked = event.target.checked;

    // Shiprocket tracking is only meaningful/available for these states.
    if (checked && !shiprocketStatusAvailable) {
      setShowShiprocketStatus(false);
      updateURL({ shiprocket_status: "" });
      return;
    }

    setShowShiprocketStatus(checked);
    updateURL({ shiprocket_status: checked ? 1 : "" });
  };

  // Auto-disable the switch when user moves to an unsupported tab.
  useEffect(() => {
    if (!shiprocketStatusAvailable && showShiprocketStatus) {
      setShowShiprocketStatus(false);
      updateURL({ shiprocket_status: "" });
    }
  }, [shiprocketStatusAvailable, showShiprocketStatus, updateURL]);

  // Clear cached shiprocket statuses when switch is turned off.
  useEffect(() => {
    if (!showShiprocketStatus) {
      setShiprocketDeliveryStatusByOrderId({});
      setTrackingDataByOrderId({});
      setShiprocketStatusLoadingByOrderId({});
    }
  }, [showShiprocketStatus]);

  // Fetch shiprocket current delivery status for visible orders.
  useEffect(() => {
    if (!filtersReady) return;
    if (!showShiprocketStatus) return;
    if (!shiprocketStatusAvailable) return;

    const ordersToFetch = mobileOrders.filter(
      (o) =>
        o?.tracking_id &&
        shiprocketDeliveryStatusByOrderId[o.id] === undefined &&
        shiprocketStatusLoadingByOrderId[o.id] !== true
    );

    if (ordersToFetch.length === 0) return;

    // Limit concurrent fetches to avoid hammering Shiprocket.
    const toFetch = ordersToFetch.slice(0, 6);

    toFetch.forEach((o) => {
      setShiprocketStatusLoadingByOrderId((prev) => ({
        ...prev,
        [o.id]: true,
      }));
    });

    Promise.all(
      toFetch.map(async (o) => {
        try {
          const courierName = (o.courier_name || "").toLowerCase();
          let trackingFullData = null;
          let response = null;
          let currentStatus = "N/A";

          if (courierName.includes("delhivery")) {
            response = await api.get(`/delhivery_tracking.php`, {
              params: { waybill: o.tracking_id },
            });
            trackingFullData = response?.data?.data;
            currentStatus = trackingFullData?.status || "In Transit";
          } else {
            // Default to Shiprocket
            response = await api.get(`/shiprocket_tracking.php`, {
              params: { action: "track", awb: o.tracking_id },
            });
            const trackingData = response?.data?.data?.tracking_data;
            const shipmentTrack = trackingData?.shipment_track?.[0] || {};

            trackingFullData = {
              status: shipmentTrack?.current_status || trackingData?.current_status,
              destination: shipmentTrack?.destination || trackingData?.destination,
              expected_delivery: trackingData?.etd || shipmentTrack?.edd
            };

            currentStatus = trackingFullData.status || "Not Sure";
          }

          return { orderId: o.id, currentStatus, trackingFullData };
        } catch (e) {
          return { orderId: o.id, currentStatus: "N/A", trackingFullData: null };
        }
      })
    ).then((results) => {
      setShiprocketDeliveryStatusByOrderId((prev) => {
        const next = { ...prev };
        results.forEach(({ orderId, currentStatus }) => {
          next[orderId] = currentStatus;
        });
        return next;
      });

      setTrackingDataByOrderId((prev) => {
        const next = { ...prev };
        results.forEach(({ orderId, trackingFullData }) => {
          if (trackingFullData) next[orderId] = trackingFullData;
        });
        return next;
      });

      setShiprocketStatusLoadingByOrderId((prev) => {
        const next = { ...prev };
        toFetch.forEach((o) => {
          next[o.id] = false;
        });
        return next;
      });
    });
  }, [
    filtersReady,
    showShiprocketStatus,
    shiprocketStatusAvailable,
    mobileOrders,
    shiprocketDeliveryStatusByOrderId,
    shiprocketStatusLoadingByOrderId,
  ]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <>
      <Helmet>
        <title>Orders Management - Ishqnama Art | Dashboard</title>
        <meta
          name="description"
          content="Manage and track all orders in your Ishqnama Art dashboard."
        />
      </Helmet>

      <Box
        sx={{
          minHeight: "100vh",
          background: "#F0F2F5",
          py: { xs: 1, md: 1 },
          px: { xs: 1.5, md: 2 },
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Mobile Header - Simple Search and Create */}
          {isMobile && (
            <OrdersMobileHeader
              search={search}
              onSearchChange={handleSearchChange}
              onCreateOrder={() => navigate("/order/create")}
              showShiprocketStatus={showShiprocketStatus}
              onShiprocketStatusChange={handleShiprocketStatusSwitch}
              shiprocketStatusDisabled={!shiprocketStatusAvailable}
            />
          )}

          {/* Desktop Header - Full Filters */}
          {!isMobile && (
            <motion.div variants={itemVariants}>
              <Box sx={{ mb: 4 }} />

              <Card
                sx={{
                  background: "rgba(255, 255, 255, 0.7)",
                  backdropFilter: 'blur(20px)',
                  borderRadius: "24px",
                  mb: 4,
                  boxShadow: "0 10px 40px rgba(0,0,0,0.03)",
                  border: "1px solid rgba(255, 255, 255, 0.5)",
                }}
              >
                <CardContent sx={{ p: 3.5 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                    }}
                  >
                    <TextField
                      variant="outlined"
                      size="small"
                      value={search}
                      onChange={handleSearchChange}
                      placeholder="Search by ID, Name or Phone..."
                      InputProps={{
                        startAdornment: (
                          <SearchIcon
                            sx={{ color: "text.secondary", mr: 1.5, fontSize: 20 }}
                          />
                        ),
                      }}
                      sx={{
                        flex: 2,
                        "& .MuiOutlinedInput-root": {
                          borderRadius: '16px',
                          background: 'rgba(0,0,0,0.02)',
                          "& fieldset": { border: 'none' },
                          "&:hover": { background: 'rgba(0,0,0,0.04)' },
                          "&.Mui-focused": { background: '#FFFFFF', boxShadow: '0 0 0 1px var(--primary-main)' }
                        },
                      }}
                    />

                    <FormControl size="small" sx={{ flex: 1 }}>
                      <Select
                        value={paymentStatusFilter}
                        displayEmpty
                        onChange={handlePaymentStatusChange}
                        sx={{
                          borderRadius: '16px',
                          background: 'rgba(0,0,0,0.02)',
                          "& .MuiOutlinedInput-notchedOutline": { border: 'none' },
                          "&:hover": { background: 'rgba(0,0,0,0.04)' },
                        }}
                      >
                        <MenuItem value="">Status: All Payments</MenuItem>
                        {paymentStatusOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {formatStatus(option)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl size="small" sx={{ flex: 1 }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          value={dateOfMarriageFilter ? dayjs(dateOfMarriageFilter) : null}
                          onChange={handleDateOfMarriageChange}
                          slotProps={{
                            textField: {
                              size: 'small',
                              variant: 'outlined',
                              placeholder: 'Marriage Date',
                              sx: {
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: '16px',
                                  background: 'rgba(0,0,0,0.02)',
                                  "& fieldset": { border: 'none' },
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </FormControl>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2, borderLeft: '1px solid rgba(0,0,0,0.1)' }}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', letterSpacing: '0.1em' }}>
                        LOGISTICS
                      </Typography>
                      <Switch
                        checked={showShiprocketStatus}
                        onChange={handleShiprocketStatusSwitch}
                        disabled={!shiprocketStatusAvailable}
                        size="small"
                      />
                    </Box>

                    <Button
                      onClick={() => navigate("/order/create")}
                      variant="contained"
                      startIcon={<AddIcon />}
                      sx={{
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #B51D6E 0%, #E91E63 100%)',
                        color: "#FFFFFF !important",
                        px: 3,
                        py: 1.2,
                        fontWeight: 900,
                        textTransform: 'none',
                        fontSize: '0.9rem',
                        minWidth: 'fit-content',
                        boxShadow: '0 8px 25px rgba(181, 29, 110, 0.2)',
                        "&:hover": {
                          background: 'linear-gradient(135deg, #E91E63 0%, #B51D6E 100%)',
                          boxShadow: '0 12px 30px rgba(181, 29, 110, 0.35)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      Create
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Status Tabs - Both Desktop and Mobile */}
          <motion.div variants={itemVariants}>
            <Card sx={{
              mb: { xs: 2, md: 4 },
              borderRadius: { xs: "16px", md: "24px" },
              overflow: "hidden",
              border: '1px solid rgba(0,0,0,0.05)',
              boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
            }}>
              <Tabs
                value={selectedStatus}
                onChange={handleStatusTabChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  background: "#FFFFFF",
                  minHeight: { xs: 50, md: 64 },
                  "& .MuiTab-root": {
                    minWidth: "auto",
                    px: { xs: 2, md: 4 },
                    py: { xs: 1.5, md: 3 },
                    fontWeight: 700,
                    textTransform: "none",
                    fontSize: { xs: "0.85rem", md: "0.95rem" },
                    color: "text.secondary",
                    transition: 'all 0.3s',
                    "&.Mui-selected": {
                      color: "primary.main",
                      backgroundColor: 'rgba(181, 29, 110, 0.04)',
                    },
                  },
                  "& .MuiTabs-indicator": {
                    height: 4,
                    borderRadius: '4px 4px 0 0',
                  },
                }}
              >
                {statusOptions.map((status) => (
                  <Tab
                    key={status}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, width: 'auto' }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          color: selectedStatus === status ? 'primary.main' : 'text.disabled',
                          transition: 'color 0.3s'
                        }}>
                          {getStatusIcon(status)}
                        </Box>
                        <span style={{ fontSize: isMobile ? '0.7rem' : '0.8rem', fontWeight: 800 }}>
                          {status === "all" ? "All Collections" : formatStatus(status)}
                        </span>
                        <Box
                          sx={{
                            px: 1,
                            minWidth: 22,
                            height: isMobile ? 18 : 22,
                            borderRadius: '6px',
                            background: selectedStatus === status ? 'linear-gradient(135deg, #B51D6E 0%, #E91E63 100%)' : 'rgba(0,0,0,0.06)',
                            color: selectedStatus === status ? '#fff' : 'text.secondary',
                            fontSize: '0.65rem',
                            fontWeight: 900,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: selectedStatus === status ? '0 4px 10px rgba(181, 29, 110, 0.2)' : 'none'
                          }}
                        >
                          {statusCounts[status] || 0}
                        </Box>
                      </Box>
                    }
                    value={status}
                  />
                ))}
              </Tabs>
            </Card>
          </motion.div>

          {/* Orders Cards View - Both Desktop and Mobile */}
          <motion.div variants={itemVariants}>
            <OrdersMobileView
              orders={mobileOrders}
              loading={loading}
              onViewOrder={handleViewOrder}
              onLoadMore={loadMoreOrders}
              hasMore={hasMore}
              loadingMore={loadingMore}
              selectedStatus={selectedStatus}
              isDesktop={!isMobile}
              onSortToggle={handleSortToggle}
              sortOrder={sortOrder}
              showShiprocketStatus={showShiprocketStatus}
              shiprocketDeliveryStatusByOrderId={shiprocketDeliveryStatusByOrderId}
              trackingDataByOrderId={trackingDataByOrderId}
              shiprocketStatusLoadingByOrderId={shiprocketStatusLoadingByOrderId}
              onRefreshOrders={() => {
                setMobilePage(1);
                setHasMore(true);
                fetchMobileOrders(1, true);
                fetchStatusCounts();
              }}
              onTrackOrder={async (order) => {
                const trackingId = order.tracking_id;
                const courierName = (order.courier_name || "").toLowerCase();

                if (courierName.includes("shipway")) {
                  setDelhiveryData(null);
                  setTrackingUrl(`https://ishqnama.shipway.com/tracking/forward/${trackingId}/`);
                  setTrackingModalOpen(true);
                } else if (courierName.includes("delhivery")) {
                  setTrackingLoading(true);
                  setTrackingModalOpen(true);
                  setTrackingUrl("");
                  try {
                    const response = await api.get(`/delhivery_tracking.php`, {
                      params: { waybill: trackingId }
                    });
                    if (response.data.success) {
                      setDelhiveryData(response.data.data);
                    }
                  } catch (error) {
                    console.error("Error fetching Delhivery tracking:", error);
                  } finally {
                    setTrackingLoading(false);
                  }
                } else {
                  setDelhiveryData(null);
                  setTrackingUrl(`https://shiprocket.co/tracking/${trackingId}`);
                  setTrackingModalOpen(true);
                }
              }}
            />
          </motion.div>
        </motion.div>
      </Box>

      {/* Tracking Modal with iframe */}
      <Dialog
        open={trackingModalOpen}
        onClose={() => setTrackingModalOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
          }}
        >
          <Typography variant="h6" component="span">
            Track Order
          </Typography>
          <IconButton
            onClick={() => setTrackingModalOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 2.5,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: '#F8FAFC'
          }}
        >
          {trackingLoading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 2 }}>
              <CircularProgress size={48} thickness={4} sx={{ color: 'primary.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>Fetching tracking details...</Typography>
            </Box>
          ) : delhiveryData ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {/* Fixed Header Card */}
              <Box sx={{ mb: 3, px: 1 }}>
                <Card sx={{ 
                  borderRadius: '24px', 
                  border: 'none', 
                  boxShadow: '0 10px 30px rgba(0,0,0,0.04)',
                  background: 'linear-gradient(135deg, #ffffff 0%, #f8faff 100%)',
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  <Box sx={{ 
                    position: 'absolute', 
                    top: 0, right: 0, width: '150px', height: '150px', 
                    background: 'radial-gradient(circle, rgba(181, 29, 110, 0.03) 0%, transparent 70%)',
                    zIndex: 0
                  }} />
                  <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Box>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', mb: 0.5 }}>
                          Current Shipment Status
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 900, color: 'primary.main', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'primary.main', boxShadow: '0 0 10px rgba(181, 29, 110, 0.4)' }} />
                          {delhiveryData.status}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <img 
                          src="https://upload.wikimedia.org/wikipedia/commons/2/23/Delhivery_Logo_%282019%29.png" 
                          alt="Delhivery" 
                          style={{ height: '16px', marginBottom: '4px' }} 
                        />
                        <Typography variant="body2" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                          #{delhiveryData.waybill}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ mb: 3, opacity: 0.5 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(181, 29, 110, 0.05)', color: 'primary.main' }}>
                            <CalendarToday sx={{ fontSize: 18 }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>Expected Delivery</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.85rem' }}>{delhiveryData.expected_delivery ? dayjs(delhiveryData.expected_delivery).format('DD MMM YYYY') : 'N/A'}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(0,0,0,0.05)', color: 'text.primary' }}>
                            <LocationOn sx={{ fontSize: 18 }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>Origin</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.85rem' }}>{delhiveryData.origin || 'N/A'}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(0,0,0,0.05)', color: 'text.primary' }}>
                            <FmdGood sx={{ fontSize: 18 }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>Destination</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.85rem' }}>{delhiveryData.destination || 'N/A'}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(0,0,0,0.05)', color: 'text.primary' }}>
                            <LocalShipping sx={{ fontSize: 18 }} />
                          </Box>
                          <Box>
                            <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', display: 'block', fontSize: '0.65rem' }}>Last Update</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800, fontSize: '0.85rem' }}>{delhiveryData.last_update ? dayjs(delhiveryData.last_update).format('DD MMM, hh:mm A') : 'N/A'}</Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Box>

              {/* Scrollable Scan History */}
              <Box sx={{ flex: 1, overflowY: 'auto', px: 2, pb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 900, mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  Journey History
                  <Chip label={`${delhiveryData.scans?.length || 0} Events`} size="small" sx={{ fontWeight: 800, fontSize: '0.65rem' }} />
                </Typography>
                
                <Box sx={{ position: 'relative', pl: { xs: 2, md: 4 } }}>
                  {/* Timeline Line */}
                  <Box sx={{ 
                    position: 'absolute', 
                    left: { xs: '25px', md: '41px' }, 
                    top: 20, bottom: 20, 
                    width: '2px', 
                    bgcolor: 'rgba(0,0,0,0.06)',
                    zIndex: 0
                  }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {delhiveryData.scans && delhiveryData.scans.length > 0 ? (
                      [...delhiveryData.scans].reverse().map((scan, idx) => {
                        const isLatest = idx === 0;
                        return (
                          <Box key={idx} sx={{ display: 'flex', gap: 3, position: 'relative', zIndex: 1 }}>
                            {/* Timeline Node */}
                            <Box sx={{ 
                              width: isLatest ? 18 : 12, 
                              height: isLatest ? 18 : 12, 
                              borderRadius: '50%', 
                              bgcolor: isLatest ? 'primary.main' : '#fff',
                              border: isLatest ? '4px solid #fff' : '2px solid rgba(0,0,0,0.15)',
                              boxShadow: isLatest ? '0 0 0 4px rgba(181, 29, 110, 0.1)' : 'none',
                              mt: 0.5,
                              flexShrink: 0
                            }} />

                            <Card sx={{ 
                              flex: 1, 
                              borderRadius: '16px', 
                              border: isLatest ? '1px solid rgba(181, 29, 110, 0.1)' : '1px solid rgba(0,0,0,0.03)',
                              boxShadow: isLatest ? '0 10px 25px rgba(181, 29, 110, 0.05)' : 'none',
                              background: isLatest ? '#fff' : 'rgba(255,255,255,0.5)',
                            }}>
                              <CardContent sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Typography variant="body1" sx={{ fontWeight: 900, color: isLatest ? 'primary.main' : 'text.primary', fontSize: '0.95rem' }}>
                                    {scan.Scan}
                                  </Typography>
                                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary', bgcolor: 'rgba(0,0,0,0.03)', px: 1, py: 0.5, borderRadius: '6px' }}>
                                    {scan.ScanDateTime ? dayjs(scan.ScanDateTime).format('DD MMM, hh:mm A') : ''}
                                  </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.8rem' }}>
                                  <LocationOn sx={{ fontSize: 14 }} />
                                  {scan.ScannedLocation}
                                </Typography>
                                {scan.ScanDetail && (
                                  <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary', fontStyle: 'italic' }}>
                                    "{scan.ScanDetail}"
                                  </Typography>
                                )}
                              </CardContent>
                            </Card>
                          </Box>
                        );
                      })
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'rgba(0,0,0,0.02)', borderRadius: '24px', border: '1px dashed rgba(0,0,0,0.1)' }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary' }}>No scanning events recorded yet.</Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : trackingUrl ? (
            <Box
              sx={{
                flex: 1,
                width: '100%',
                height: '100%',
                border: 'none',
                overflow: 'hidden',
                borderRadius: '16px'
              }}
            >
              <iframe
                src={trackingUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  minHeight: '600px',
                }}
                title="Track Order"
                allow="fullscreen"
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography variant="body2" color="text.secondary">No tracking data available.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Orders;
